import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, RefreshControl, ScrollView } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import Button from '../../src/components/Button';
import Card from '../../src/components/Card';
import ContentContainer from '../../src/components/ContentContainer';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';

interface Location {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly isActive: boolean;
  readonly latitude: number | null;
  readonly longitude: number | null;
}

interface NwsPeriod {
  startTime: string;
  endTime?: string;
  temperature: number;
  temperatureUnit: string;
  shortForecast: string;
  probabilityOfPrecipitation?: { value: number | null };
}
interface WeatherToday {
  locationId: string;
  forecast: { properties?: { periods?: NwsPeriod[] } };
}

/** Location stat row from dashboard analytics API. */
interface LocationStat {
  readonly id: string;
  readonly name: string;
  readonly bayCount: number;
  readonly activeBays: number;
  readonly totalCharged: number;
  readonly totalWashes: number;
  readonly todayWashes: number;
  readonly todayCharged: number;
}

interface Dashboard {
  readonly locationStats: LocationStat[];
  readonly activeSessions: number;
}

/** CENTS_DIVISOR - convert cents to dollars display. */
const CENTS_DIVISOR = 100;

/** OwnerLocationsScreen - displays owner's locations with today's stats. */
export default function OwnerDashboardScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [locations, setLocations] = useState<Location[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [weatherByLoc, setWeatherByLoc] = useState<Record<string, NwsPeriod>>({});
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const toast = useToast();
  const { isOffline } = useNetwork();

  const fetchData = async () => {
    let dashFailed = false;
    try {
      const [locs, dash] = await Promise.all([
        api<Location[]>('/api/locations'),
        api<Dashboard>('/api/analytics/dashboard').catch(() => { dashFailed = true; return null; }),
      ]);
      setLocations(locs);
      setDashboard(dash);
      if (dashFailed && !isOffline) {
        toast.show({ type: 'warning', title: 'Dashboard stats failed to load' });
      }

      // Fetch today's weather for each geocoded location in parallel
      const wxResults = await Promise.all(locs
        .filter(l => l.latitude != null && l.longitude != null)
        .map(async l => {
          try {
            const r = await api<WeatherToday>(`/api/analytics/weather/today?locationId=${l.id}`);
            const period = r.forecast?.properties?.periods?.[0];
            return period ? { id: l.id, period } : null;
          } catch { return null; }
        }));
      const wxMap: Record<string, NwsPeriod> = {};
      for (const w of wxResults) if (w) wxMap[w.id] = w.period;
      setWeatherByLoc(wxMap);
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  /** Pick an icon name based on NWS short forecast text. */
  const weatherIcon = (s: string): keyof typeof Ionicons.glyphMap => {
    const t = s.toLowerCase();
    if (t.includes('thunder') || t.includes('storm')) return 'thunderstorm';
    if (t.includes('rain') || t.includes('shower') || t.includes('drizzle')) return 'rainy';
    if (t.includes('snow')) return 'snow';
    if (t.includes('cloud') || t.includes('overcast')) return 'cloudy';
    if (t.includes('partly')) return 'partly-sunny';
    return 'sunny';
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  /** Format cents as dollar string. */
  const fmt = (cents: number) => `$${(cents / CENTS_DIVISOR).toFixed(2)}`;

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}
    >
      <ContentContainer>
      {/* Active Sessions Banner */}
      {dashboard && dashboard.activeSessions > 0 && (
        <Card style={styles.liveCard}>
          <View style={styles.liveRow}>
            <View style={styles.liveDot} />
            <Text style={styles.liveText}>{dashboard.activeSessions} active wash{dashboard.activeSessions > 1 ? 'es' : ''} right now</Text>
          </View>
        </Card>
      )}

      {/* Locations */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>My Locations</Text>
        <TouchableOpacity onPress={() => router.push('/owner/add-location' as never)}>
          <View style={styles.addBtn}>
            <Ionicons name="add-circle" size={32} color={colors.primary} />
          </View>
        </TouchableOpacity>
      </View>

      <Button
        title="Wash Tiers & Upsells"
        onPress={() => router.push('/owner/pricing/washes-upsells' as never)}
        icon={<Ionicons name="pricetags-outline" size={20} color={colors.primary} />}
        variant="outline"
        style={{ marginBottom: spacing.md }}
      />

      {locations.map(loc => {
        const stat = dashboard?.locationStats.find(s => s.id === loc.id);
        const wx = weatherByLoc[loc.id];
        return (
          <TouchableOpacity key={loc.id} onPress={() => router.push(`/owner/location/${loc.id}` as never)}>
            <Card style={styles.locationCard}>
              <View style={styles.locationRow}>
                <View style={styles.locationIcon}>
                  <Ionicons name="water" size={24} color={colors.primary} />
                </View>
                <View style={styles.locationInfo}>
                  <Text style={styles.locationName}>{loc.name}</Text>
                  <Text style={styles.locationAddress}>{loc.city}, {loc.state}</Text>
                </View>
                {wx && (
                  <View style={styles.wxChip}>
                    <Ionicons name={weatherIcon(wx.shortForecast)} size={16} color={colors.primary} />
                    <Text style={styles.wxText}>{wx.temperature}°{wx.temperatureUnit}</Text>
                  </View>
                )}
                <TouchableOpacity
                  onPress={() => router.push(`/owner/location/edit?locationId=${loc.id}` as never)}
                  hitSlop={8}
                  style={{ padding: spacing.xs }}
                >
                  <Ionicons name="pencil" size={18} color={colors.primary} />
                </TouchableOpacity>
                <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
              </View>
              {wx && (
                <Text style={styles.wxDetail}>
                  {wx.shortForecast}
                  {(wx.probabilityOfPrecipitation?.value ?? 0) > 0 && ` · ${wx.probabilityOfPrecipitation?.value}% precip`}
                </Text>
              )}
              {stat && (
                <View style={styles.locationStats}>
                  <View style={styles.locationStat}>
                    <Text style={styles.locationStatValue}>{stat.bayCount}</Text>
                    <Text style={styles.locationStatLabel}>Bays</Text>
                  </View>
                  <View style={styles.locationStatDivider} />
                  <View style={styles.locationStat}>
                    <Text style={styles.locationStatValue}>{stat.todayWashes}</Text>
                    <Text style={styles.locationStatLabel}>Today</Text>
                  </View>
                  <View style={styles.locationStatDivider} />
                  <View style={styles.locationStat}>
                    <Text style={[styles.locationStatValue, { color: colors.success }]}>{fmt(stat.todayCharged)}</Text>
                    <Text style={styles.locationStatLabel}>Sales</Text>
                  </View>
                  {stat.activeBays > 0 && (
                    <>
                      <View style={styles.locationStatDivider} />
                      <View style={styles.locationStat}>
                        <View style={styles.activeDot} />
                        <Text style={styles.locationStatLabel}>{stat.activeBays} In Use</Text>
                      </View>
                    </>
                  )}
                </View>
              )}
            </Card>
          </TouchableOpacity>
        );
      })}

      {locations.length === 0 && !loading && (
        <Card style={styles.emptyCard}>
          <Ionicons name="location-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>No locations yet</Text>
          <Text style={styles.emptyText}>Add your first car wash location to start accepting payments</Text>
          <TouchableOpacity style={styles.emptyBtn} onPress={() => router.push('/owner/add-location' as never)}>
            <Ionicons name="add" size={20} color={colors.primary} />
            <Text style={styles.emptyBtnText}>Add Location</Text>
          </TouchableOpacity>
        </Card>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },

  liveCard: { marginBottom: spacing.md, backgroundColor: c.success + '10', borderWidth: 1, borderColor: c.success + '30' },
  liveRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  liveDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: c.success },
  liveText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.success },

  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  addBtn: { padding: spacing.sm },

  locationCard: { marginBottom: spacing.md },
  locationRow: { flexDirection: 'row', alignItems: 'center' },
  wxChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: 4, backgroundColor: c.primaryBg, borderRadius: borderRadius.full, marginRight: spacing.xs },
  wxText: { fontSize: fontSize.xs, color: c.primaryDark, fontWeight: fontWeight.semibold },
  wxDetail: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 6, marginLeft: rs(48) + spacing.md },
  locationIcon: {
    width: rs(48), height: rs(48), borderRadius: rs(24),
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center', marginRight: spacing.md,
  },
  locationInfo: { flex: 1 },
  locationName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  locationAddress: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  locationStats: {
    flexDirection: 'row', marginTop: spacing.md, paddingTop: spacing.md,
    borderTopWidth: 1, borderTopColor: c.borderLight, justifyContent: 'space-around',
  },
  locationStat: { alignItems: 'center' },
  locationStatValue: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  locationStatLabel: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  locationStatDivider: { width: 1, backgroundColor: c.borderLight, marginHorizontal: spacing.sm },
  activeDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: c.success, marginBottom: 2 },

  emptyCard: { alignItems: 'center', paddingVertical: spacing.xxl },
  emptyTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: 280 },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.lg, paddingVertical: spacing.sm, paddingHorizontal: spacing.md },
  emptyBtnText: { fontSize: fontSize.md, color: c.primaryDark, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
